import {
  ArrowLeftRight,
  Building2,
  CreditCard,
  Gauge,
  LifeBuoy,
  LogOut,
  ScrollText,
  Settings,
  Ticket,
  Users,
  Wrench,
  Bug,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { LogoMark } from '@/components/brand/Logo';
import { ThemeToggle } from '@/components/layout/Toggles';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useAuth } from '@/providers/AuthProvider';

/**
 * Оболочка админ-панели.
 *
 * Визуально отличается от обычного приложения намеренно: тёмная шапка и
 * метка ADMIN. Администратор часто держит открытыми обе части, и спутать
 * «свой воркспейс» с «чужими данными платформы» здесь дорого.
 */

const SECTIONS = [
  { to: '/admin', end: true, labelKey: 'admin:navOverview', icon: Gauge },
  { to: '/admin/users', labelKey: 'admin:navUsers', icon: Users },
  { to: '/admin/workspaces', labelKey: 'admin:navWorkspaces', icon: Building2 },
  { to: '/admin/plans', labelKey: 'admin:navPlans', icon: CreditCard },
  { to: '/admin/invites', labelKey: 'admin:navInvites', icon: Ticket },
  { to: '/admin/support', labelKey: 'admin:navSupport', icon: LifeBuoy },
  { to: '/admin/audit', labelKey: 'admin:navAudit', icon: ScrollText },
  { to: '/admin/errors', labelKey: 'admin:navErrors', icon: Bug },
  { to: '/admin/settings', labelKey: 'admin:navSettings', icon: Settings },
  { to: '/admin/maintenance', labelKey: 'admin:navMaintenance', icon: Wrench },
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['admin', 'common']);
  const { me, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg">
      <header className="sticky top-0 z-30 border-b border-border bg-[#101014] text-white">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-3 px-4">
          <span className="flex items-center gap-2 font-semibold">
            <LogoMark className="h-5 w-5" decorative />
            {t('common:appName')}
          </span>
          <span className="rounded bg-danger px-1.5 py-0.5 text-[10.5px] font-bold tracking-wider">
            ADMIN
          </span>

          <span className="ml-auto hidden text-[13px] text-white/70 sm:block">
            {me?.user.displayName}
          </span>

          <ThemeToggle />

          {/* Заметная кнопка, а не бледная ссылка: выйти из панели в приложение
              нужно часто, и искать её глазами по тёмной шапке не должно приходиться. */}
          <Button
            variant="secondary"
            size="sm"
            icon={<ArrowLeftRight />}
            className="border-white/20 bg-white/10 text-white hover:bg-white/20"
            onClick={() => navigate('/')}
          >
            {t('admin:toApp')}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:logout')}
            className="text-white/80 hover:bg-white/10 hover:text-white"
            onClick={() => void logout()}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 py-6 lg:flex lg:gap-6">
        <nav className="hidden w-[210px] shrink-0 flex-col gap-0.5 lg:flex">
          {SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] transition-colors',
                  isActive
                    ? 'bg-accent-soft font-semibold text-accent'
                    : 'text-muted hover:bg-surface-2 hover:text-text',
                )
              }
            >
              <section.icon className="h-[15px] w-[15px] shrink-0" aria-hidden />
              {t(section.labelKey)}
            </NavLink>
          ))}
        </nav>

        {/* На узком экране разделы едут горизонтальной лентой: таблицы всё равно
            прокручиваются вбок, и вертикальное меню только съедало бы ширину. */}
        <nav className="-mx-4 mb-4 flex gap-1 overflow-x-auto px-4 pb-2 lg:hidden">
          {SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.end}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[13px]',
                  isActive ? 'bg-accent-soft font-semibold text-accent' : 'text-muted',
                )
              }
            >
              {t(section.labelKey)}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
